import {
  SelectionType,
  SelfAssessmentStep,
  Validator,
  WordSelectionMode,
} from "~/classes/models/self-assessment/self-assessment-step.model";
import { getPlacementValidations, getPlacementParagraphPositions } from "~/utils/self-assessment-placement";
import type { SelfAssessmentPlacement } from "~/classes/models/self-assessment/self-assessment-step.model";
import { AttrRange } from "../quill-selection/useAttributeInstances";
import { useCountWords } from "../quill-selection/useCountWords";
import { useParagraphSentences } from "../quill-selection/useParagraphSentences";
import { useRelevantInstanceSentence } from "../quill-selection/useRelevantinstanceSentence";
import { useSetInstanceError } from "./useSetInstanceError";
import { useAllParagraphs } from "../quill-selection/useAllParagraphs";
import { analyzeDocumentBlocks, snapshotDocumentBlockInputs } from "../document-analysis/analyzeDocumentBlocks";

const validateDuplicateAttributes = (
  instance: any,
  duplicateAttributePrefix: string
) => {
  const attributes = instance.attributes;
  if (attributes == undefined) {
    return;
  }

  const attributeKeys = Object.keys(attributes);
  if (attributeKeys.length == 0) {
    return;
  }

  const attributesWithPrefix = attributeKeys.filter((key) => {
    return key.startsWith(duplicateAttributePrefix);
  });

  if (attributesWithPrefix.length > 1) {
    throw new Error("duplicate-attribute-error");
  }
};

const validateValidationAttributes = (
  validationAttributes: {
    [key: string]: boolean | (string | undefined)[] | Validator | undefined;
  },
  instance: any
) => {
  let allAttributesMatch = true;
  let validInstance = 0;

  let hasMatchingAttribute = false;

  if (validationAttributes == undefined) {
    return;
  }

  if (Object.keys(validationAttributes).length == 0) {
    return;
  }

  for (const [attribute, value] of Object.entries(validationAttributes)) {
    if (instance.attributes == undefined) {
      continue;
    }

    if (value == Validator.isNotNull) {
      if (instance.attributes[attribute] != undefined) {
        validInstance += 1;
        hasMatchingAttribute = true;
        break;
      }
    }

    // At least one attribute must match
    if (Array.isArray(value)) {
      let attributeValue = instance.attributes[attribute];

      if (attributeValue == undefined) {
        attributeValue = null;
      }

      if (value.includes(attributeValue)) {
        validInstance += 1;
        hasMatchingAttribute = true;
        break;
      }
    }

    if (typeof value == "boolean") {
      if (instance.attributes[attribute] == value) {
        validInstance += 1;
        hasMatchingAttribute = true;
        break;
      }
    }
  }

  if (validInstance == 0) {
    throw new Error("formatting-error");
  }
};

const validateLastSentencePlacement = (
  instance: any,
  bodyParagraphs: {
    index: number;
    data: any;
  }[],
  revisionOps: any[],
  quill: any
) => {
  const paragraphIndex = instance.paragraphIndex;

  if (paragraphIndex == undefined) {
    throw new Error("placement-error");
  }

  const lastBodyParagraphIndex =
    bodyParagraphs[bodyParagraphs.length - 1]?.index;

  if (paragraphIndex != lastBodyParagraphIndex) {
    throw new Error("placement-error");
  }

  const paragraphSentences = useParagraphSentences(
    revisionOps,
    quill,
    paragraphIndex,
    { checkItalic: true }
  );

  if (paragraphSentences == undefined) {
    throw new Error("placement-error");
  }

  const relevantInstanceSentence = useRelevantInstanceSentence(
    paragraphSentences,
    instance
  );

  if (relevantInstanceSentence == undefined) {
    throw new Error("placement-error");
  }

  const lastSentenceIndex = paragraphSentences.length - 1;
  const validSentenceIndexes = new Set([lastSentenceIndex]);

  if (
    paragraphSentences.length > 1 &&
    paragraphSentences[0].isItalic &&
    paragraphSentences[lastSentenceIndex].isItalic
  ) {
    validSentenceIndexes.add(lastSentenceIndex - 1);
  }

  if (!validSentenceIndexes.has(relevantInstanceSentence.sentenceIndex)) {
    throw new Error("placement-error");
  }
};

const validateTopicClinchersPlacement = (
  instance: any,
  revisionOps: any[],
  quill: any,
  bodyParagraphs: {
    index: number;
    data: any;
  }[],
  paragraphPositions: number[]
) => {
  const paragraphIndex = instance.paragraphIndex;

  if (paragraphIndex == undefined) {
    throw new Error("placement-error");
  }

  const paragraphSentences = useParagraphSentences(
    revisionOps,
    quill,
    paragraphIndex,
    { checkItalic: true }
  );

  if (paragraphSentences == undefined) {
    throw new Error("placement-error");
  }

  const relevantInstanceSentence = useRelevantInstanceSentence(
    paragraphSentences,
    instance
  );

  if (relevantInstanceSentence == undefined) {
    throw new Error("placement-error");
  }

  // By default we check the fisrst and last sentence to make sure the instance is in the correct place.
  let firstReviewableSentenceIndex = 0;
  let lastReviewableSentenceIndex = paragraphSentences.length - 1;

  // If the first AND last sentence are completely italic then we check the second and second to last sentence.
  if (
    paragraphSentences[firstReviewableSentenceIndex].isItalic &&
    paragraphSentences[lastReviewableSentenceIndex].isItalic
  ) {
    firstReviewableSentenceIndex = 1;
    lastReviewableSentenceIndex = paragraphSentences.length - 2;
  }

  // If the relevant instance is not in the first or last sentence, throw an error
  if (
    relevantInstanceSentence.sentenceIndex != firstReviewableSentenceIndex &&
    relevantInstanceSentence.sentenceIndex != lastReviewableSentenceIndex
  ) {
    throw new Error("placement-error");
  }
};

const validateInstanceParagraphPlacement = (
  instance: any,
  revisionOps: any[],
  quill: any,
  bodyParagraphs: {
    index: number;
    data: any;
  }[],
  paragraphPositions: number[]
) => {
  const relevantInstanceParagraphIndex = instance.paragraphIndex;

  if (relevantInstanceParagraphIndex == undefined) {
    throw new Error("placement-error");
  }

  const relevantParagraphs = paragraphPositions
    .map((position) => {
      return bodyParagraphs[position - 1];
    })
    .filter((paragraph) => paragraph != undefined)
    .map((paragraph) => paragraph.index);

  if (relevantParagraphs.length == 0) {
    throw new Error("placement-error");
  }

  const isInstanceInRelevantParagraphs = relevantParagraphs.includes(
    relevantInstanceParagraphIndex
  );

  if (isInstanceInRelevantParagraphs != true) {
    throw new Error("placement-error");
  }
};

const validateMultipleWordInstances = (instance: AttrRange) => {
  const text = instance.textSnippet;

  if (text == undefined) {
    return;
  }

  const wordCount = useCountWords(text);

  if (wordCount > 1) {
    throw new Error("multiple-word-instances-error");
  }
};

const validateSentenceOpenerMarker = ({
  step,
  instance,
  revisionOps,
  quill,
}: {
  step: SelfAssessmentStep;
  instance: any;
  revisionOps: any[];
  quill: any;
}) => {
  if (step.sentenceOpenerMarkerValidation == undefined) {
    return;
  }

  const paragraphIndex = instance.paragraphIndex;

  if (paragraphIndex == undefined) {
    throw new Error("formatting-error");
  }

  const paragraphSentences = useParagraphSentences(
    revisionOps,
    quill,
    paragraphIndex
  );

  if (paragraphSentences == undefined) {
    throw new Error("formatting-error");
  }

  const instanceSentence = useRelevantInstanceSentence(
    paragraphSentences,
    instance
  );

  if (instanceSentence == undefined) {
    throw new Error("formatting-error");
  }

  const sentenceText = instanceSentence.text.trim();

  if (sentenceText.startsWith(step.sentenceOpenerMarkerValidation) != true) {
    throw new Error("formatting-error");
  }
};

const validatePlacement = (
  placement: SelfAssessmentPlacement,
  instance: any,
  bodyParagraphs: { index: number; data: any }[],
  paragraphPositions: number[],
  revisionOps: any[],
  quill: any
) => {
  if (placement === "title") {
    const paragraphs = useAllParagraphs(quill);
    const titleNode = paragraphs[instance.paragraphIndex]?.domNode as HTMLElement | undefined;
    const isIdentifiedTitle = titleNode?.hasAttribute("data-title-paragraph") ||
      !!titleNode?.querySelector("[data-title-paragraph]");
    const { blocks } = analyzeDocumentBlocks(snapshotDocumentBlockInputs(paragraphs));
    if (!isIdentifiedTitle && !blocks.some(block =>
      block.kind === "title" && block.sourceIndex === instance.paragraphIndex
    )) throw new Error("placement-error");
    return;
  }

  if (placement === "lastSentence") {
    validateLastSentencePlacement(instance, bodyParagraphs, revisionOps, quill);
    return;
  }

  if (placement === "topicClinchers") {
    validateTopicClinchersPlacement(instance, revisionOps, quill, bodyParagraphs, paragraphPositions);
  }

  validateInstanceParagraphPlacement(
    instance, revisionOps, quill, bodyParagraphs,
    getPlacementParagraphPositions(placement, paragraphPositions)
  );
};

const validateInstance = (
  step: SelfAssessmentStep,
  instance: any,
  bodyParagraphs: {
    index: number;
    data: any;
  }[],
  paragraphPositions: number[],
  revisionOps: any[],
  quill: any
) => {
  // Check if the step has a duplicate attribute prefix
  // Then validate for duplicates on this instance if needed.
  let duplicateAttributePrefix = step.duplicateAttributePrefix;

  if (duplicateAttributePrefix != undefined) {
    validateDuplicateAttributes(instance, duplicateAttributePrefix);
  }

  // Here is where I'll change how this applies in a moment.
  if (
    step.wordSelectionMode != WordSelectionMode.multipleWords &&
    step.selectionType == SelectionType.token
  ) {
    validateMultipleWordInstances(instance);
  }

  const placements = getPlacementValidations(step);
  if (placements.length > 0) {
    const isAllowed = placements.some(placement => {
      try {
        validatePlacement(placement, instance, bodyParagraphs, paragraphPositions, revisionOps, quill);
        return true;
      } catch (error) {
        if (error instanceof Error && error.message === "placement-error") return false;
        throw error;
      }
    });
    if (!isAllowed) throw new Error("placement-error");
  }

  if (step.validationAttributes != undefined) {
    validateValidationAttributes(step.validationAttributes, instance);
  }
};

// Both selection-time checks and full step assessment use the same feedback.
export const useSelfAssessmentValidateInstance = (
  ...args: Parameters<typeof validateInstance>
) => {
  const [step, instance, , , , quill] = args;
  try {
    validateInstance(...args);
  } catch (error) {
    useSetInstanceError(quill, step.criteriaAttribute?.attribute, instance.value);
    throw error;
  }

  useSetInstanceError(
    quill,
    step.criteriaAttribute?.attribute,
    instance.value,
    false
  );
};
